<template>
  <div class="consulting-page">
    <section class="page-hero">
      <div class="container">
        <h1>技术服务</h1>
        <p>从数据规划、主数据治理到定制化数据获取，我们以「咨询 + 实施」的方式陪跑，让数据能力真正落在业务流程里，而不止停在方案文档上。</p>
        <div class="hero-btns">
          <router-link class="btn btn-ghost hero-btn-light" to="/contact">预约方案沟通</router-link>
          <router-link class="btn btn-ghost hero-btn-outline" to="/api">查看我们的数据产品</router-link>
        </div>
      </div>
    </section>

    <!-- 数据一览 -->
    <section class="section alt">
      <div class="container">
        <div class="stats">
          <div class="stat" v-for="item in stats" :key="item.label">
            <b>{{ item.value }}</b>
            <span>{{ item.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 01 服务内容 -->
    <section class="band">
      <div class="container">
        <div class="band-head">
          <span class="band-num">01</span>
          <h2>服务内容</h2>
          <span class="rule"></span>
        </div>
        <p class="band-lead">围绕数据从「理清楚」到「用起来」的完整链路，提供六类技术服务，可整体推进，也可按需单点切入。</p>
        <div class="grid grid-2">
          <div class="card" v-for="item in services" :key="item.title">
            <div class="icon" v-html="item.icon"></div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
            <router-link class="learn-more" to="/contact">方案咨询 <span class="arrow">→</span></router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- 02 我们可以帮您 -->
    <section class="band band-alt">
      <div class="container">
        <div class="band-head">
          <span class="band-num">02</span>
          <h2>我们可以帮您</h2>
          <span class="rule"></span>
        </div>
        <p class="band-lead">咨询不是交一份报告，而是把方法、工具和交付物一起交到团队手上。</p>
        <div class="grid grid-3">
          <div class="card value-card" v-for="item in values" :key="item.title">
            <div class="icon" v-html="item.icon"></div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 03 标杆案例 -->
    <section class="band">
      <div class="container">
        <div class="band-head">
          <span class="band-num">03</span>
          <h2>标杆案例</h2>
          <span class="rule"></span>
        </div>
        <p class="band-lead">从主数据管理到定制化数据获取，我们为生物制药、标准研究、政务园林、金融信用等行业客户交付了可验证的成果。</p>
        <div class="grid grid-2">
          <div class="card case-card" :class="{ 'case-card-feat': item.feat }" v-for="item in cases" :key="item.title">
            <span class="case-tag">{{ item.tag }}</span>
            <h3>{{ item.title }}</h3>
            <p class="case-body" v-html="item.body"></p>
          </div>
        </div>
      </div>
    </section>

    <!-- 04 合作流程 -->
    <section class="band band-alt">
      <div class="container">
        <div class="band-head">
          <span class="band-num">04</span>
          <h2>合作流程</h2>
          <span class="rule"></span>
        </div>
        <p class="band-lead">从第一次沟通到长期运营，每一步都有明确的交付物与验收标准。</p>
        <div class="steps">
          <div class="step" v-for="item in flow" :key="item.title">
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="section">
      <div class="container">
        <div class="cta cta-photo">
          <h2>先聊聊您当下的数据难题</h2>
          <p>一次 30 分钟的沟通，我们会给出初步的判断与建议路径，不收取费用。</p>
          <router-link class="btn" to="/contact">预约方案沟通</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
/* 技术服务页（原「咨询服务」）：结构、文案与图标均按 website-handoff/consulting.html 落地。
   页面为纯静态内容，不发起任何后端请求（留言与预约入口统一指向 /contact）。 */
const ICON = {
  plan: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="width:26px;height:26px;"><circle cx="12" cy="12" r="9"/><path d="M12 3v18M3 12h18"/><circle cx="12" cy="12" r="3"/></svg>',
  master: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="width:26px;height:26px;"><ellipse cx="12" cy="5.5" rx="8" ry="2.8"/><path d="M4 5.5v6c0 1.55 3.58 2.8 8 2.8s8-1.25 8-2.8v-6"/><path d="M4 11.5v6c0 1.55 3.58 2.8 8 2.8s8-1.25 8-2.8v-6"/></svg>',
  govern: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="width:26px;height:26px;"><path d="M12 2.2l7.5 2.8v6.1c0 4.8-3.3 8.6-7.5 10.7C7.8 19.7 4.5 15.9 4.5 11.1V5z"/><path d="M8.6 11.8l2.4 2.4 4.4-4.6"/></svg>',
  custom: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="width:26px;height:26px;"><path d="M4 6.5C4 5.1 5.1 4 6.5 4h11C18.9 4 20 5.1 20 6.5v11c0 1.4-1.1 2.5-2.5 2.5h-11C5.1 20 4 18.9 4 17.5z"/><path d="M8 9h8M8 12.5h5M8 16h3"/></svg>',
  smart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="width:26px;height:26px;"><path d="M12 3l1.9 4.6L18.5 9l-3.4 3.5.8 4.9L12 15l-3.9 2.4.8-4.9L5.5 9l4.6-1.4z"/><path d="M19 17.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"/></svg>',
  platform: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="width:26px;height:26px;"><rect x="3" y="4" width="18" height="6" rx="2"/><rect x="3" y="14" width="18" height="6" rx="2"/><path d="M7 7h.01M7 17h.01"/></svg>',
  facts: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="width:24px;height:24px;"><path d="M3 20h18M6.5 20V10M12 20V4M17.5 20v-7"/></svg>',
  threshold: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="width:24px;height:24px;"><path d="M12 3v6M12 15v6M3 12h6M15 12h6"/><circle cx="12" cy="12" r="3"/></svg>',
  quick: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="width:24px;height:24px;"><path d="M4 17l5-5 3.5 3.5L20 8"/><path d="M15 8h5v5"/></svg>'
}

export default {
  name: 'consulting',
  data() {
    return {
      stats: [
        { value: '100+', label: '企业终端用户' },
        { value: '20+', label: '覆盖行业' },
        { value: '200+', label: '标准/可定制化接口' },
        { value: '4亿+', label: '覆盖主体数量' }
      ],
      services: [
        {
          icon: ICON.plan,
          title: '数据规划与咨询',
          desc: '盘点数据资产、梳理业务口径、设计指标体系，输出可执行的数据建设路线图，让后续每一步投入都有依据。'
        },
        {
          icon: ICON.master,
          title: '主数据管理咨询与实施',
          desc: '面向物料、供应商、客户、项目/WBS、人员等主数据域，完成数据规划、标准制定、治理规则设计与系统集成，打通 ERP、CRM、SRM、WMS、MES 等业务系统。'
        },
        {
          icon: ICON.govern,
          title: '数据治理与数据标准',
          desc: '建立数据标准、元数据与质量管理体系，统一口径、沉淀血缘，让数据可查、可溯源、可理解；结合大模型能力完成语义打标与规范文档的自动生成。'
        },
        {
          icon: ICON.custom,
          title: '定制化数据获取与加工',
          desc: '面向公开数据源与客户指定数据源的采集、解析、清洗去重与结构化入表，按客户规范交付，支持增量比对与持续更新。'
        },
        {
          icon: ICON.smart,
          title: '数据智能应用建设',
          desc: '把治理好的数据接入业务场景：智能监控与告警、指标看板、风险识别与自动报告，让数据从「看得见」走向「用得上」。'
        },
        {
          icon: ICON.platform,
          title: '数据平台建设咨询',
          desc: '湖仓一体架构规划、技术选型与实施陪跑，从存储、计算、治理到服务分层设计，避免重复建设与数据烟囱。'
        }
      ],
      values: [
        {
          icon: ICON.facts,
          title: '让决策建立在事实之上',
          desc: '统一指标口径、明确数据来源，让业务复盘和经营决策都有同一套数字可依，不再各说各话。'
        },
        {
          icon: ICON.threshold,
          title: '降低数据使用门槛',
          desc: '不必先组建一支大数据团队：平台、工具与方法一并交付，现有业务人员也能把数据用起来。'
        },
        {
          icon: ICON.quick,
          title: '小场景切入，快速见效',
          desc: '从主数据整理、客商风险监控等具体场景起步，先跑出可量化的结果，再逐步扩展到全链路治理。'
        }
      ],
      cases: [
        {
          feat: true,
          tag: '商业查询数据供应',
          title: '企业多维度信息定制与交付',
          body: '持续深耕商业数据监控、数据调用与数据技术服务领域。可根据用户需求提供全国市场主体工商与司法数据的采集、清洗、去重与标准化供给：覆盖 <b>4亿+</b> 主体、<b>7.5亿条</b>司法信息等；可通过 API <b>实时查询</b>与批量离线包（CSV / JSON）等多种方式交付，支撑平台端的工商档案、司法涉诉、知识产权等查询场景。'
        },
        {
          feat: true,
          tag: '主数据管理',
          title: '企业主数据管理平台',
          body: '为某生物医药公司、某医药集团建设主数据体系，覆盖物料、供应商、客户、项目/WBS、人员等<b>主数据域</b>，完成数据规划、治理、系统集成与接口开发，打通 ERP、CRM、SRM、WMS、MES 等<b>十余个业务系统</b>，统一服务调度，集成业务、数据、技术<b>三大平台接口</b>；降低数据治理门槛，支持多条线业务人员参与数据管道设计与数据分析；提升数据时效性，支撑<b>业务创新</b>与<b>快速决策</b>。'
        },
        {
          tag: '关联关系数据服务',
          title: '商业查询平台企业关联图谱数据',
          body: '服务各大机构用户及信用平台，基于工商股权关联信息、司法关联方、共有知识产权等数据搭建企业<b>关系图谱</b>，向上递归追溯股东至自然人或国资主体、向下穿透至<b>末级运营主体</b>，自动识别合伙企业 GP、累计计算<b>多层持股</b>比例并检测循环持股；输出实际控制人、受益所有人、疑似关联方等标签，供平台的关联排查、尽调与风控场景直接调用。'
        },
        {
          tag: '数据治理 + AI',
          title: '储能产业经贸规则数据治理',
          body: '为某研究机构开发基于大模型的 AI 工具，对 WTO 通报文件、美国 CPSC / 欧盟 RAPEX 召回数据、多国法规数据进行智能阅读与语义打标，并按客户规范自动生成定制化 Word 报告。支持对结构化数据库 RDS、DRDS、PolarDB、OceanBase、ECS 自建数据库，非结构化数据存储 OSS、OTS，大数据平台 MaxCompute 的<b>数据审计与防护</b>；支持敏感数据识别后，<b>一键开启</b>列加密和图片脱敏进行治理。'
        }
      ],
      flow: [
        { title: '需求沟通', desc: '了解业务目标、现有系统与数据现状，明确本次咨询要解决的核心问题。' },
        { title: '诊断与方案设计', desc: '输出数据现状诊断、目标蓝图与实施方案，包含范围、里程碑与交付物清单。' },
        { title: '试点验证', desc: '选取一个高价值场景先做试点，用真实数据验证方案可行性后再全面推广。' },
        { title: '实施与交付', desc: '按方案完成建设、集成与数据迁移，交付文档、规范与培训，确保团队接得住。' },
        { title: '持续运营', desc: '提供运维支持与迭代优化，随业务变化持续完善标准、指标与应用。' }
      ]
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/consulting/index.vue